import { SEOHead } from "components/SEO"
import { generateProductPageGraphSchema } from "utils/structuredData"
import HeroSection from "components/pages/embedded-dashboards/HeroSection"
import InteractiveAnalyticsAnywhereSection from "components/pages/embedded-dashboards/InteractiveAnalyticsAnywhereSection"
import SingleCTASection from "components/pages/embedded-dashboards/SingleCTASection"
import UseCasesSection from "components/pages/embedded-dashboards/UseCasesSection"
import FAQSection from "components/pages/embedded-dashboards/FAQSection"
import DividerSection from "components/ui/Blocks/DividerSection"
import Layout from "components/ui/Layout"

// FAQs for embedded analytics (used for schema markup)
const embeddedDashboardsFaqs = [
  {
    question: "What is embedded analytics?",
    answer:
      "Embedded analytics is the integration of data visualizations, dashboards, and reporting capabilities directly into your own software applications. Instead of users switching to a separate BI tool, they access analytics within your product's interface.",
  },
  {
    question: "How do I embed Preset dashboards in my application?",
    answer:
      "Preset offers multiple embedding options: iframe embedding for simple use cases, and the Embedded SDK for full control. The SDK supports React, Vue, Angular, and vanilla JavaScript. You generate a guest token from your backend and pass it to the SDK to securely render dashboards.",
  },
  {
    question: "Is embedded analytics secure for multi-tenant applications?",
    answer:
      "Yes. Preset supports Row-Level Security (RLS) which ensures each customer only sees their own data. Combined with guest tokens that encode user context, you can safely embed the same dashboard for multiple customers while keeping their data isolated.",
  },
  {
    question: "Can I customize the look of embedded dashboards?",
    answer:
      "Yes. Preset embedded dashboards support extensive customization including hiding Preset UI elements, custom CSS themes to match your brand, configurable filter visibility, and responsive sizing.",
  },
  {
    question: "What's the difference between iframe and SDK embedding?",
    answer:
      "Iframe embedding is simpler but offers less control. The SDK provides programmatic control so you can respond to filter changes, communicate between your application and the dashboard, handle authentication more securely, and customize the experience dynamically.",
  },
  {
    question: "Do embedded dashboards work on mobile devices?",
    answer:
      "Yes. Preset dashboards are responsive and work on mobile devices. The Embedded SDK handles responsive sizing automatically.",
  },
  {
    question: "How does guest token authentication work?",
    answer:
      "Your backend requests a guest token from Preset's API, specifying which dashboards the user can access and optionally their row-level security context. This short-lived token is passed to the frontend SDK, which uses it to render the dashboard securely.",
  },
  {
    question: "Can users interact with embedded dashboards?",
    answer:
      "Yes. Embedded dashboards are fully interactive. Users can apply filters, drill down into data, hover for tooltips, and explore within the embedded experience.",
  },
]

function EmbeddedDashboards({ location: { pathname } }) {
  return (
    <Layout
      contentSpaced
      trackingName="Embedded Dashboards"
      pageName="embedded-dashboards"
      pagePath={pathname}
    >
      <HeroSection />
      <InteractiveAnalyticsAnywhereSection />
      <UseCasesSection />
      <FAQSection />
      <DividerSection />
      <SingleCTASection />
    </Layout>
  )
}

// Export a Head component for Gatsby Head API
export function Head() {
  const graphSchema = generateProductPageGraphSchema({
    pageUrl: "https://preset.io/embedded-dashboards/",
    pageTitle: "Embedded Dashboards | Preset",
    productName: "Preset Embedded Dashboards",
    description:
      "Preset Embedded Dashboards helps teams integrate interactive analytics directly into internal tools and customer-facing applications with secure embedding, customization, and scalable access control.",
    featureList: [
      "Embedded dashboards for internal tools and customer-facing applications",
      "Embedded SDK",
      "Iframe embedding",
      "40+ visualization types",
      "Scales from 0 to thousands of users",
      "Row-level security",
      "SSO and SAML integration",
      "Guest token authentication",
      "Custom CSS and theming",
      "Responsive dashboard embedding",
    ],
    faqs: embeddedDashboardsFaqs,
    offers: {
      "@type": "Offer",
      url: "https://preset.io/embedded-dashboards/",
      availability: "https://schema.org/InStock",
      category: "Embedded Analytics Software",
      description:
        "Preset Embedded Dashboards is available for teams that want to integrate analytics into their applications. Request access for details.",
    },
    breadcrumbs: [
      { name: "Home", url: "https://preset.io/" },
      {
        name: "Embedded Dashboards",
        url: "https://preset.io/embedded-dashboards/",
      },
    ],
  })

  return (
    <SEOHead
      title="Embedded Analytics Dashboard for SaaS Products"
      description="Add an embedded analytics dashboard to your product with Preset. Deliver interactive insights to customers, powered by Apache Superset. Book a demo."
      structuredData={[graphSchema]}
    />
  )
}

export default EmbeddedDashboards
